import { useEffect, useMemo } from 'react';

import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { PromptSave } from '../promptSave';
import { CompanyDetails } from './CompanyDetails';
import type { CompanyDetailsFormValues } from './CompanyDetails.types';
import { createCompanySchema } from './formValidation/companySchema';

type Props = {
    initialValues: CompanyDetailsFormValues;
    companyType: { value: string; label: string };
    openPrompt: boolean;
    submitting: boolean;
    onSubmit: (values: CompanyDetailsFormValues) => void;
    onPromptOpen: () => void;
    onPromptCancel: () => void;
};

export const CompanyDetailsForm = ({
    initialValues,
    companyType,
    openPrompt,
    submitting,
    onSubmit,
    onPromptOpen,
    onPromptCancel
}: Props) => {
    const intl = useIntl();

    const schema = useMemo(
        () => createCompanySchema(intl, companyType, getCountry()),
        [intl, companyType]
    );

    const methods = useForm<CompanyDetailsFormValues>({
        defaultValues: initialValues
    });

    const {
        reset,
        getValues,
        setError,
        clearErrors,
        formState: { isDirty }
    } = methods;

    useEffect(() => {
        if (isDirty && !openPrompt) onPromptOpen();
    }, [isDirty, openPrompt, onPromptOpen]);

    const handleSubmitPrompt = () => {
        const values = getValues();
        clearErrors();
        try {
            schema.validateSync(values, { abortEarly: false });
            onSubmit(values);
            // Re-baseline so isDirty clears; otherwise the dirty effect
            // re-opens the prompt right after the saga's CANCEL_PROMPT.
            reset(values, { keepValues: true });
        } catch (err) {
            // Drive validation synchronously inside the click handler. RHF's
            // own handleSubmit would await yupResolver, landing setError() in
            // a later microtask — too late for tests that read errors right
            // after clicking Save.
            if (err instanceof yup.ValidationError) {
                for (const inner of err.inner) {
                    if (inner.path) {
                        setError(
                            inner.path as FieldPath<CompanyDetailsFormValues>,
                            {
                                type: inner.type ?? 'validate',
                                message: inner.message
                            }
                        );
                    }
                }
            }
        }
    };

    const handleCancelPrompt = () => {
        reset(initialValues);
        // Defer so the Cancel button stays mounted for the same tick as the
        // click; the prompt unmounts on the next tick via CANCEL_PROMPT.
        setTimeout(onPromptCancel, 0);
    };

    return (
        <>
            <FormProvider {...methods}>
                <CompanyDetails />
            </FormProvider>
            <PromptSave
                openPrompt={openPrompt}
                handleSubmitPrompt={handleSubmitPrompt}
                handleCancelPrompt={handleCancelPrompt}
                loading={submitting}
            />
        </>
    );
};
